import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import IconContainer from "@/components/ui/icon-container";
import {
  Bullet,
  ChevronDown,
  Contracts,
  SquareArrowUpRight
} from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { companyStatusColors } from "@/pages/companies/Companies";
import { CompanyStatus } from "@/pages/companies/types";
import { ContractTableStatus } from "@/pages/contracts/types";
import { useContract } from "@/pages/contracts/hooks";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import {
  TabOption,
  TAB_SEARCH_PARAM,
  getTabSlug
} from "@/components/layouts/DetailedView";
import { IconButton } from "@/components/ui/icon-button";
import { CompanyContractRow } from "../types";
import { useFinancials } from "@/services/hooks/useFinancials";
import { Commercials } from "@/pages/deals/types";
import Spinner from "@/components/ui/spinner";
import FinancialRecognitionSection from "./FinancialRecognitionSection";
import ServicesSection from "./ServicesSection";

const TRANSLATION_PREFIX = "companies.commercials";

// formatCurrency now defaults to showing cents; this page keeps whole units
// until its own precision is reviewed on its own terms.
const WHOLE_UNITS = 0;

interface Props {
  contract: CompanyContractRow;
}

const DealContractCard: React.FC<Props> = ({ contract }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { navigateToDetailedView } = useDealsNavigation();
  // Financial recognition + services aren't in the filter row; fetch them once expanded.
  const {
    data: fullContract,
    isLoading: isLoadingFullContract,
    isError: isFullContractError,
    refetch: refetchFullContract
  } = useContract(contract.id, { enabled: isExpanded });

  const currency = contract.currency ?? "USD";
  // The sign override is the whole point of useFinancials here: it is the
  // contract's sign already resolved against the account's. Discarding it and
  // re-deriving from `currency` left a USD contract on a CAD account rendering
  // `$1,234 ($5,678)`, the bug this ticket was filed against.
  //
  // The hook gets the RAW contract currency, not the "USD"-defaulted `currency`
  // above: a contract with no currency recorded must reach the hook as
  // unresolved so its guard suppresses conversion, rather than being compared
  // as though it were USD. `currency` stays the display fallback.
  //
  // It goes only to FinancialRecognitionSection, which prints an account
  // figure beside each contract one. The summary rows below are left bare
  // deliberately: that same row ends with an explicit `<code> (Currency)`
  // chip, so the glyph is not the only thing naming the currency there and
  // qualifying it would only repeat what the chip already says.
  const { accountCurrencySymbol, contractCurrencySignOverride, showConversion } =
    useFinancials({ currency: contract.currency } as Commercials);
  const isActive = contract.status === ContractTableStatus.ACTIVE;

  const renewalDate = contract.nextRenewal
    ? simpleDate(toLocalDate(contract.nextRenewal))
    : null;

  return (
    <div
      className="flex flex-col rounded-xl cursor-pointer border border-border-widget-card bg-surface-widget-card"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div
        className={cn(
          "flex p-5 justify-between items-center w-full",
          isExpanded ? "border-b border-border-border1" : "border-none"
        )}
      >
        <div className="flex gap-4 items-center">
          <IconButton
            variant="iconNoBg"
            className="mt-1"
            iconClassName={cn(
              "transition-transform duration-200",
              !isExpanded && "-rotate-90"
            )}
          >
            <ChevronDown />
          </IconButton>
          <div className="flex flex-col gap-1">
            <div className="flex gap-2 items-center">
              <IconContainer size="5">
                <Contracts />
              </IconContainer>
              <div className="flex items-center gap-4">
                <Text variant="lgSubtitleH1">{contract.name}</Text>
                <Badge
                  text={contract.statusName}
                  icon={
                    <IconContainer
                      style={{
                        color:
                          companyStatusColors[
                            isActive
                              ? CompanyStatus.ACTIVE_CUSTOMER
                              : CompanyStatus.DISQUALIFIED
                          ]
                      }}
                    >
                      <Bullet />
                    </IconContainer>
                  }
                />
              </div>
            </div>
            <div className="flex gap-6 items-center">
              {renewalDate && (
                <Text variant="bodySecondary">
                  {t(`${TRANSLATION_PREFIX}.renewOn`, { date: renewalDate })}
                </Text>
              )}
              {contract.productName && (
                <Text variant="bodySecondary">
                  {contract.productName} ({t(`${TRANSLATION_PREFIX}.product`)})
                </Text>
              )}
              {contract.mrr != null && contract.mrr !== 0 && (
                <Text variant="bodySecondary">
                  {formatCurrency(contract.mrr, currency, WHOLE_UNITS)} (
                  {t(`${TRANSLATION_PREFIX}.mrr`)})
                </Text>
              )}
              {contract.totalContractValue != null &&
                contract.totalContractValue !== 0 && (
                  <Text variant="bodySecondary">
                    {formatCurrency(
                      contract.totalContractValue,
                      currency,
                      WHOLE_UNITS
                    )}{" "}
                    ({t(`${TRANSLATION_PREFIX}.tcv`)})
                  </Text>
                )}
              <Text variant="bodySecondary">
                {currency} ({t(`${TRANSLATION_PREFIX}.currency`)})
              </Text>
            </div>
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            if (contract.dealId) {
              navigateToDetailedView(contract.dealId, {
                [TAB_SEARCH_PARAM]: getTabSlug(TabOption.COMMERCIALS)
              });
            }
          }}
        >
          <IconContainer size="5">
            <SquareArrowUpRight />
          </IconContainer>
          {t(`${TRANSLATION_PREFIX}.editInDealsPage`)}
        </Button>
      </div>

      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-in-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          {isLoadingFullContract ? (
            <div className="w-full flex justify-center items-center py-8">
              <Spinner size="md" />
            </div>
          ) : isFullContractError ? (
            <div className="flex flex-col gap-2 items-center py-8">
              <Text variant="bodySecondary">
                {t(`${TRANSLATION_PREFIX}.contractDetailsError`)}
              </Text>
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  refetchFullContract();
                }}
              >
                {t(`${TRANSLATION_PREFIX}.retry`)}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              <FinancialRecognitionSection
                mrr={fullContract?.mrrLocal ?? 0}
                arr={fullContract?.arrLocal ?? 0}
                acv={fullContract?.annualContractValueLocal ?? 0}
                tcv={fullContract?.totalContractValueLocal ?? 0}
                currency={currency}
                contractCurrencySignOverride={contractCurrencySignOverride}
                convertedMrr={
                  showConversion ? (fullContract?.mrr ?? 0) : undefined
                }
                convertedArr={
                  showConversion ? (fullContract?.arr ?? 0) : undefined
                }
                convertedAcv={
                  showConversion
                    ? (fullContract?.annualContractValue ?? 0)
                    : undefined
                }
                convertedTcv={
                  showConversion
                    ? (fullContract?.totalContractValue ?? 0)
                    : undefined
                }
                accountCurrencySymbol={
                  showConversion ? accountCurrencySymbol : undefined
                }
              />

              <ServicesSection
                revenueItems={fullContract?.revenueItems ?? []}
                currency={currency}
                productId={fullContract?.productId ?? undefined}
                contractId={fullContract?.id}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealContractCard;
